import { useEffect, useMemo, useState } from 'react'
import { Card, Form, Spinner, Badge } from 'react-bootstrap'
import { useQuery } from '@apollo/client'
import { FormattedMessage, useIntl } from 'react-intl'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { OrganizationChannelConfigPanel } from './components/OrganizationChannelConfigPanel'
import { CHANNEL_CONFIG_MESSAGES as M } from './channelConfigMessages'

export const ChannelConfigPage = () => {
    const intl = useIntl()
    const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
    const currentUserOrgId = useGetUser((s) => (s.user?.user as any)?.organization?.id)
    const isSuperAdmin = userGroups.includes('SuperAdministrador')
    const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

    // Organization selector state
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined)
    const { data: orgData, loading: loadingOrgs } = useQuery(GET_ALL_ORGANIZATIONS, {
        variables: { first: 100 },
        skip: !isSuperAdmin,
    })
    const organizations = useMemo(
        () => orgData?.allOrganizations?.edges?.map((e: any) => e.node) || [],
        [orgData]
    )

    // Set initial org once loaded
    useEffect(() => {
        if (isSuperAdmin && organizations.length > 0 && !selectedOrgId) {
            const matchedOrg = currentUserOrgId
                ? organizations.find((o: any) => parseId(o.id) === parseId(currentUserOrgId))
                : undefined
            const defaultOrg = matchedOrg?.id || organizations[0]?.id
            setSelectedOrgId(defaultOrg)
        }
    }, [isSuperAdmin, organizations, selectedOrgId, currentUserOrgId])

    // Decode relay ID to numeric ID for the REST API
    const decodedOrgId = selectedOrgId ? String(parseId(selectedOrgId)) : undefined

    const handleOrgChange = (orgId: string) => {
        setSelectedOrgId(orgId)
    }

    if (isSuperAdmin && loadingOrgs) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="container-xxl py-5">
                <div className="alert alert-danger d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill fs-2 me-3"></i>
                    <div>
                        <h4 className="alert-heading">{intl.formatMessage(M.permissionDeniedTitle)}</h4>
                        <p className="mb-0">{intl.formatMessage(M.permissionDeniedBody)}</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container-xxl py-5">
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="fs-2 fw-bold text-gray-900 mb-1">{intl.formatMessage(M.pageTitle)}</h1>
                    <p className="text-muted fs-6 mb-0">{intl.formatMessage(M.pageSubtitle)}</p>
                </div>
            </div>

            {/* Organization Selector — SuperAdmin only */}
            {isSuperAdmin && (
                <Card className="mb-5 shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)' }}>
                    <Card.Body className="py-4">
                        <div className="d-flex align-items-center gap-4">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-building fs-3 text-primary"></i>
                                <span className="fw-bold text-gray-800 fs-5">{intl.formatMessage(M.organization)}</span>
                            </div>
                            <Form.Select
                                value={selectedOrgId || ''}
                                onChange={(e) => handleOrgChange(e.target.value)}
                                className="mw-400px"
                                style={{ borderColor: '#c5d2e8' }}
                            >
                                <option value="" disabled>
                                    {intl.formatMessage(M.selectOrganization)}
                                </option>
                                {organizations.map((org: any) => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {selectedOrgId && (
                                <Badge bg="light" text="dark" className="fs-7 px-3 py-2 border">
                                    <FormattedMessage
                                        {...M.configuringOrg}
                                        values={{
                                            name: organizations.find(
                                                (o: any) => parseId(o.id) === parseId(selectedOrgId)
                                            )?.name,
                                            b: (chunks) => <strong>{chunks}</strong>,
                                        }}
                                    />
                                </Badge>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            <OrganizationChannelConfigPanel organizationId={decodedOrgId} key={decodedOrgId} />
        </div>
    )
}
